import React, { useEffect, useRef, useState } from 'react';
import { t } from 'ttag';
import { VisuallyHidden } from '~/lib/a11y/VisuallyHidden';

/**
 * Announces card position after Prev / Next / pagination (CAT-1435 / EL–E284).
 * Empty on first paint so initial load is not noisy (same pattern as SlideChangeAnnouncer).
 * Label avoids the word "slide" per audit guidance.
 */
export const CardChangeAnnouncer = ({
  index,
  total
}: {
  index: number;
  total: number;
}) => {
  const [message, setMessage] = useState('');
  const previousIndexRef = useRef<number | null>(null);
  /** Ignore index churn from mount / indexForStart restore (WCAG 4.1.3). */
  const readyRef = useRef(false);

  useEffect(() => {
    const settle = window.setTimeout(() => {
      readyRef.current = true;
    }, 300);
    return () => window.clearTimeout(settle);
  }, []);

  useEffect(() => {
    if (total <= 0) return;
    if (!readyRef.current) {
      previousIndexRef.current = index;
      return;
    }

    const previous = previousIndexRef.current;
    previousIndexRef.current = index;

    if (previous === null) return;
    if (previous === index) return;

    const current = index + 1;
    setMessage('');
    const timer = window.setTimeout(() => {
      setMessage(t`${current} of ${total}`);
    }, 50);

    return () => window.clearTimeout(timer);
  }, [index, total]);

  return (
    <VisuallyHidden role="status" aria-live="polite" aria-atomic="true">
      {message}
    </VisuallyHidden>
  );
};
